import React from 'react';
import { CheckCircle, ArrowRight, Shield, Zap, Users } from 'lucide-react';
import Navbar from './Navbar';
import MachineMarketfilter from './MachineMarketfilter';
import CTA from './CTA';
import Footer from './Footer';

const AbnerComponent = () => {
  return (
    <>
      <Navbar />
      <section className="relative w-full overflow-hidden font-sans antialiased min-h-[450px] md:h-[450px]">
        {/* ---------- BACKGROUND IMAGE (professional & premium) ---------- */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1400&q=80"
            alt="Industrial machinery background"
            className="h-full w-full object-cover object-center"
            loading="eager"
          />
          {/* dark gradient overlays for depth & readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-800/50 to-slate-900/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
          {/* premium accent glow */}
          <div className="absolute -top-20 -right-20 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-64 w-64 rounded-full bg-cyan-500/10 blur-2xl" />
        </div>

        {/* ---------- CONTENT (on top of background) ---------- */}
        <div className="relative z-10 flex h-full min-h-[450px] items-center px-4 sm:px-6 lg:px-8 py-6 md:py-0">
          <div className="mx-auto w-full max-w-7xl">
            <div className="flex flex-col lg:flex-row items-center gap-6 md:gap-8 lg:gap-12">
              
              {/* ---------- LEFT SIDE: TEXT CONTENT ---------- */}
              <div className="flex-1 w-full lg:max-w-xl space-y-3 md:space-y-4 text-white">
                {/* Badge / tag */}
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white text-[10px] sm:text-xs font-semibold px-2.5 sm:px-3 py-1 rounded-full border border-white/20 shadow-lg shadow-black/10">
                  <Shield size={12} className="text-cyan-300" />
                  <span>Next‑gen platform</span>
                </div>

                {/* Heading */}
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">
                  <span className="block text-white drop-shadow-lg">342+ Machines.</span>
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-cyan-200 to-blue-400 drop-shadow-lg">
                    One platform.
                  </span>
                </h1>

                {/* Description */}
                <p className="text-xs sm:text-sm md:text-base text-white/90 leading-relaxed max-w-lg drop-shadow-md">
                  Unify your entire machine ecosystem. From edge devices to cloud 
                  analytics — all in one secure, intelligent workspace.
                </p>

                {/* Feature bullets - hidden on very small screens, shown on sm and up */}
                <ul className="space-y-1.5 sm:space-y-2 text-white/95 hidden sm:block">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle size={16} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-xs sm:text-sm md:text-base font-medium drop-shadow">Centralized device orchestration</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Zap size={16} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-xs sm:text-sm md:text-base font-medium drop-shadow">Real‑time performance insights</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Users size={16} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-xs sm:text-sm md:text-base font-medium drop-shadow">Role‑based access for teams</span>
                  </li>
                </ul>

                {/* Feature bullets - simplified for mobile */}
                <ul className="space-y-1 text-white/95 sm:hidden">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-[11px] font-medium drop-shadow">Centralized orchestration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap size={14} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-[11px] font-medium drop-shadow">Real-time insights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Users size={14} className="text-cyan-300 mt-0.5 flex-shrink-0 drop-shadow" />
                    <span className="text-[11px] font-medium drop-shadow">Team access control</span>
                  </li>
                </ul>

                {/* CTA + premium hint */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-1">
                  <button className="group inline-flex items-center gap-2 bg-white hover:bg-blue-50 text-slate-900 font-semibold px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl shadow-2xl shadow-blue-900/40 transition-all duration-200 hover:shadow-blue-700/30 hover:scale-[1.02] backdrop-blur-sm text-xs sm:text-sm">
                    <span>Get started</span>
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <span className="text-[10px] sm:text-xs bg-white/20 backdrop-blur-md px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-white/30 flex items-center gap-1.5 text-white shadow-lg">
                    <span className="text-yellow-300 text-[10px] sm:text-xs">★</span> 
                    <span className="font-medium text-[10px] sm:text-xs">Premium</span>
                    <span className="text-white/60 hidden sm:inline text-[10px] sm:text-xs">·</span>
                    <span className="text-white/80 hidden sm:inline text-[10px] sm:text-xs">professional grade</span>
                  </span>
                </div>

                {/* small trust line */}
                <p className="text-[9px] sm:text-[10px] text-white/70 flex items-center gap-1.5 drop-shadow">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                  Trusted by 2,000+ industrial teams
                </p>
              </div>

              {/* ---------- RIGHT SIDE: IMAGE CARD (hidden on mobile, visible on md+) ---------- */}
              <div className="hidden md:flex flex-1 w-full lg:max-w-lg justify-center lg:justify-end">
                <div className="relative w-full max-w-sm md:max-w-md">
                  {/* elegant card frame with glass effect */}
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/40 ring-1 ring-white/20 bg-white/10 backdrop-blur-md p-1">
                    <div className="rounded-xl overflow-hidden bg-slate-200/20">
                      <img
                        src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop&crop=center&auto=format&q=80"
                        alt="Professional industrial machines and control panel"
                        className="w-full h-auto aspect-[4/3] object-cover transition-transform duration-700 hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* floating badge – premium accent */}
                  <div className="absolute -bottom-2 -right-2 bg-white/90 backdrop-blur-md rounded-xl px-3 py-2 shadow-2xl border border-white/50 flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center text-white shadow-md">
                      <Zap size={16} fill="white" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Pro</p>
                      <p className="text-xs font-bold text-slate-800">Premium access</p>
                    </div>
                  </div>

                  {/* small floating dot – design detail */}
                  <div className="absolute -top-2 -left-2 w-12 h-12 bg-blue-400/30 rounded-full blur-2xl -z-10" />
                  <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-cyan-300/20 rounded-full blur-3xl -z-10" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="mb-5 mt-6 md:mb-[20px] md:mt-[30px]">
        <MachineMarketfilter />
        <CTA />
        <Footer />
      </div>
    </>
  );
};

export default AbnerComponent;